import { CommonModule } from "@angular/common";
import {
	Component,
	ElementRef,
 	OnInit,
	ViewChild,
	afterNextRender,
	inject,
	model,
	signal,
	Injector,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { SvgIconComponent } from "angular-svg-icon";
import { DialogModule } from "primeng/dialog";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import type { ChatHistory, RequestPrompt } from "../../../core/models/prompts";
import type { ChatOption } from "../../../core/models/prompts";
import { PromptService } from "../../services/prompt/prompt.service";

@Component({
    selector: "app-ai-chat",
	standalone: true,
    imports: [
        InputIconModule,
        IconFieldModule,
        InputTextModule,
        FormsModule,
        DialogModule,
        SvgIconComponent,
        CommonModule,
    ],
    templateUrl: "./ai-chat.component.html",
    styleUrl: "./ai-chat.component.scss"
})
export class AiChatComponent implements OnInit {
	@ViewChild("scrollMe") private myScrollContainer!: ElementRef;

	promptService = inject(PromptService);
	private injector = inject(Injector);
	constructor() {}
	loading = signal<boolean>(false);
	options: ChatOption[] = [];
	errorOptions: ChatOption[] = [];
	prompt = "";
	visible = model(false);
	isExpanded = false;
	chatHistory: ChatHistory[] = [];
	downloadHistory: ChatHistory[] = [];
	optionIndex: number | undefined;
	sessionId = signal<string>('');
	userName = "";
	showDialog() {
		const user = localStorage.getItem("userState");
		if (!user) return;
		this.userName = JSON.parse(user).family_name;
		this.visible.set(true);
	}
	ngOnInit(): void {
		this.options = [
			{
				name: "Orders",
				value: [
					"How do I resend an order?",
					"What statuses can an order be in?",
					"What can I filter the order results by?",
				],
			},
			{
				name: "Card Catalog",
				value: [
					"How can I create a new card?",
					"What can I filter the card results by?",
					'What does "Saved" and "Published" mean?',
				],
			},
			{
				name: "Users",
				value: [
					"How do I create a user?",
					`How do I reset a user's password?`,
					"What are user privileges?",
				],
			},
			{
				name: "Accounts",
				value: [
					"How do I approve an account?",
					"What information is displayed when reviewing an account?",
					"How do I disable an account?",
				],
			},
			{
				name: "Schedules",
				value: [
					"How do I define the shipping methods on my storefront?",
					"How do I set order approvers?",
					"How do I set discount tiers?",
				],
			},
			{
				name: "Publisher",
				value: ["What is the publisher?", "How do I publish an item?"],
			},
		];
	}

	selectMain(index: number) {
		if (this.optionIndex === index) {
			this.optionIndex = undefined;
		} else {
			this.optionIndex = index;
		}
		afterNextRender(() => {
			this.scrollToBottom();
		}, { injector: this.injector });
	}
	selectOption(prompt: string) {
		// console.log(prompt);
		afterNextRender(() => {
			this.scrollToBottom();
		}, { injector: this.injector });
		if (prompt) {
			const requestPrompt: RequestPrompt = {
				requestPrompt: prompt,
				sessionId: this.sessionId() ? this.sessionId() : '',
			};
			this.errorOptions = [];
			this.chatHistory = [
				...this.chatHistory,
				{ role: "user", message: prompt },
			];
			this.downloadHistory.push({ role: "user", message: prompt });
			this.loading.set(true);
			this.promptService.getPromopt(requestPrompt).subscribe(
				(data: any) => {
					const removeStartAndEnd = data.responsePrompt.trim();
					const response = removeStartAndEnd.replaceAll("\n", "<br>");
					// console.log(data);
					this.downloadHistory.push({
						role: "bot",
						message: data.responsePrompt,
					});
					this.chatHistory.push({ role: "bot", message: response });
					this.sessionId.set(data.sessionId);
					this.loading.set(false);
					afterNextRender(() => {
						this.scrollToBottom();
					}, { injector: this.injector });
				},
				(error) => {
					this.chatHistory.push({
						role: "bot",
						message: `I'm unable to answer that question. Try asking another or choose one of the following topics:`,
					});
					this.loading.set(false);
					this.errorOptions = this.options;
					// console.log(error);
					afterNextRender(() => {
						this.scrollToBottom();
					}, { injector: this.injector });
				},
				
			);
		}
	}
	clearChatData() {
		this.chatHistory = [];
		this.prompt = "";
		this.isExpanded = false;
		this.sessionId.set('');
		this.closeDialog();
	}
	showExpanded() {
		this.isExpanded = !this.isExpanded;
	}
	closeDialog() {
		this.visible.set(false)
	}
	enterPrompt() {
		// console.log(this.prompt);
		if (this.prompt) {
			const requestPrompt: RequestPrompt = {
				requestPrompt: this.prompt,
				sessionId: this.sessionId() ? this.sessionId() : '',
			};
			this.errorOptions = [];

			this.chatHistory.push({ role: "user", message: this.prompt });
			this.downloadHistory.push({ role: "user", message: this.prompt });
			this.prompt = "";
			this.loading.set(true);
			this.promptService.getPromopt(requestPrompt).subscribe(
				(data: any) => {
					const removeStartAndEnd = data.responsePrompt.trim();
					const response = removeStartAndEnd.replaceAll("\n", "<br>");
					// console.log(data);
					this.downloadHistory.push({
						role: "bot",
						message: data.responsePrompt,
					});
					this.sessionId.set(data.sessionId);
					this.chatHistory.push({ role: "bot", message: response });
					this.loading.set(false);
					afterNextRender(() => {
						this.scrollToBottom();
					}, { injector: this.injector });
				},
				(error) => {
					this.chatHistory.push({
						role: "bot",
						message: `I'm unable to answer that question. Try asking another or choose one of the following topics:`,
					});
					this.loading.set(false);
					this.errorOptions = this.options;
					// console.log(error);
					afterNextRender(() => {
						this.scrollToBottom();
					}, { injector: this.injector });
				},
				
			);
		}
	}
	scrollToBottom(): void {
			this.myScrollContainer.nativeElement.scrollTop =
				this.myScrollContainer.nativeElement.scrollHeight;
	}
	download() {
		const element = document.createElement("a");
		const temp: string[] = this.downloadHistory.map((chat) => {
			return chat.role === "bot"
				? `Answer:${chat.message}\n`
				: `Question:${chat.message}\n`;
		});
		const file = new Blob(temp, { type: "text/plain" });
		element.href = URL.createObjectURL(file);
		element.download = "chat-history.txt";
		document.body.appendChild(element); // Required for this to work in FireFox
		element.click();
	}
}
